import { BehaviorSubject, map, takeUntil } from 'rxjs'

import { BillingMode, BillingUsageReport, DeviceBillingItem, PricingPlans } from '@ti/contracts'
import { ReportColumn } from '@ti/core/common/contracts'
import { DataGridColumnType } from '@ti/core/contracts/data-grid'
import { ListModel } from '@ti/core/models/list.model'
import { ListModelConfig, ListModelState } from '@ti/core/models/types'

export abstract class BaseBillingModel extends ListModel<DeviceBillingItem> {
  public readonly invoice$ = new BehaviorSubject<BillingUsageReport | null>(null)
  public readonly baseColumns = [
    'deviceName',
    'deviceIdentifier',
    'fleetAccounts',
    'isVideo',
    'amount',
  ]
  public readonly annualColumns = [
    'currentPricingPlan',
    'assignmentDate',
    'renewalDate',
    'annualRate',
  ]
  protected abstract readonly modelName: string
  protected billingMode$ = new BehaviorSubject<BillingMode>(BillingMode.Monthly)
  protected accountBillingMode?: BillingMode

  abstract load(): Promise<void>

  /**
   * Mode the account is configured with today. Used only as a fallback: it is the wrong answer
   * for a historical report billed under a different mode.
   */
  public setAccountBillingMode(mode?: BillingMode) {
    this.accountBillingMode = mode
  }

  /**
   * The report wins over the account's current configuration so a frozen historical invoice
   * renders under the mode it was actually billed with.
   */
  protected applyBillingMode(invoice: BillingUsageReport | null) {
    this.billingMode$.next(invoice?.billingMode ?? this.accountBillingMode ?? BillingMode.Monthly)
  }

  protected async loadCount() {
    return 0
  }

  protected override loadState(): ListModelState<DeviceBillingItem> {
    const state = super.loadState()
    return {
      ...state,
      columns$: this.createGridColumnsStream(),
    }
  }

  protected createGridColumnsStream() {
    return this.billingMode$.pipe(
      takeUntil(this.destroy$),
      map((billingMode) => {
        return this.loadColumnsForBillingMode(billingMode)
      }),
    )
  }

  protected override initExportColumns(): ReportColumn[] {
    const billingMode = this.billingMode$.value
    return this.loadColumnsForBillingMode(billingMode)
  }

  protected loadColumnsForBillingMode(billingMode: BillingMode) {
    if (billingMode === BillingMode.Annual) {
      return this.props.gridColumns.filter(
        (column) =>
          this.baseColumns.includes(column.field) || this.annualColumns.includes(column.field),
      )
    } else {
      return this.props.gridColumns.filter(
        (column) =>
          this.baseColumns.includes(column.field) || !this.annualColumns.includes(column.field),
      )
    }
  }

  protected async loadPage(): Promise<DeviceBillingItem[]> {
    if (!this.invoice$.value) {
      await this.load()
    }
    // invoices loaded as single page atm.
    this.store.loadedPages.clear()

    return this.invoice$.value?.items ?? []
  }

  protected override async convertDataToExport(data: DeviceBillingItem[]) {
    const { yes, no, tnt, standard, advanced, premium } = await this.languageService.massTranslate({
      yes: 'common.yes',
      no: 'common.no',
      tnt: `tsp-admin.fleets.pricing-plans.${PricingPlans.TnT}`,
      standard: `tsp-admin.fleets.pricing-plans.${PricingPlans.Standard}`,
      advanced: `tsp-admin.fleets.pricing-plans.${PricingPlans.Advanced}`,
      premium: `tsp-admin.fleets.pricing-plans.${PricingPlans.Premium}`,
    })

    const plansMap: Record<number, string> = {
      [PricingPlans.TnT]: tnt,
      [PricingPlans.Standard]: standard,
      [PricingPlans.Advanced]: advanced,
      [PricingPlans.Premium]: premium,
    }

    const billingMode = this.billingMode$.value

    return data.map((item) => {
      if (billingMode === BillingMode.Annual) {
        return {
          deviceName: item.deviceName,
          deviceIdentifier: item.deviceIdentifier,
          fleetAccounts: this.formatFleetAccounts(item),
          isVideo: item.isVideo ? yes : no,
          currentPricingPlan: plansMap[item.currentPricingPlan] ?? standard,
          assignmentDate: item.plans[item.currentPricingPlan]?.assignmentDate ?? '',
          renewalDate: item.plans[item.currentPricingPlan]?.renewalDate ?? '',
          annualRate: item.currentPlanAssignment?.rate ?? 0,
          amount: item.amount,
        }
      } else {
        return {
          deviceName: item.deviceName,
          deviceIdentifier: item.deviceIdentifier,
          fleetAccounts: this.formatFleetAccounts(item),
          isVideo: item.isVideo ? yes : no,
          tntDays: item.plans[PricingPlans.TnT]?.daysCharged ?? 0,
          standardDays: item.plans[PricingPlans.Standard]?.daysCharged ?? 0,
          advancedDays: item.plans[PricingPlans.Advanced]?.daysCharged ?? 0,
          premiumDays: item.plans[PricingPlans.Premium]?.daysCharged ?? 0,
          extraHistoryYears: item.extraHistoryYears,
          extraMediaStorageGb: item.extraMediaStorageGb,
          extraMediaTrafficGb: item.extraMediaTrafficGb ?? 0,
          amount: item.amount,
        }
      }
    })
  }

  protected formatFleetAccounts(item: DeviceBillingItem) {
    return (item.fleetAccounts ?? [])
      .map((fleetAccount) => fleetAccount.name)
      .filter(Boolean)
      .join(', ')
  }

  protected config(): ListModelConfig {
    const baseColumns = [
      {
        field: 'deviceName',
        label: 'tsp-admin.devices.grid.device-name',
        type: DataGridColumnType.Text,
        sortable: false,
      },
      {
        field: 'deviceIdentifier',
        label: 'tsp-admin.devices.grid.imei-serial',
        type: DataGridColumnType.Text,
        sortable: false,
      },
      {
        field: 'fleetAccounts',
        label: 'tsp-admin.billing.grid.fleet-account',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'isVideo',
        label: 'tsp-admin.billing.grid.video',
        type: DataGridColumnType.YesNoStatus,
        sortable: false,
      },
    ]

    const monthlyColumns = [
      {
        field: 'tntDays',
        label: 'tsp-admin.billing.grid.tnt-days',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'standardDays',
        label: 'tsp-admin.billing.grid.standard-days',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'advancedDays',
        label: 'tsp-admin.billing.grid.advanced-days',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'premiumDays',
        label: 'tsp-admin.billing.grid.premium-days',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'extraHistoryYears',
        label: 'tsp-admin.billing.grid.extra-history-years',
        type: DataGridColumnType.Count,
        sortable: false,
      },
      {
        field: 'extraMediaStorageGb',
        label: 'tsp-admin.billing.grid.extra-storage-gb',
        type: DataGridColumnType.Count,
        sortable: false,
      },
      {
        field: 'extraMediaTrafficGb',
        label: 'tsp-admin.billing.grid.extra-media-traffic-gb',
        type: DataGridColumnType.Count,
        sortable: false,
      },
    ]

    const annualColumns = [
      {
        field: 'currentPricingPlan',
        label: 'tsp-admin.billing.grid.pricing-plan',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'assignmentDate',
        label: 'tsp-admin.billing.grid.assignment-date',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'renewalDate',
        label: 'tsp-admin.billing.grid.renewal-date',
        type: DataGridColumnType.Template,
        sortable: false,
      },
      {
        field: 'annualRate',
        label: 'tsp-admin.billing.grid.annual-rate',
        type: DataGridColumnType.Template,
        sortable: false,
      },
    ]

    return {
      name: this.modelName,
      gridColumns: [
        ...baseColumns,
        ...monthlyColumns,
        ...annualColumns,
        {
          field: 'amount',
          label: `tsp-admin.billing.grid.amount-usd`,
          type: DataGridColumnType.Currency,
          sortable: false,
        },
      ],
    }
  }
}
